// Tutorial narrator orchestrator.
//
// One ticked-per-render-frame state machine that decides what the narrator
// should say next. Reads tutorialFlow's phase, tracks side state (NPC KO,
// pit-rebound triggered, player position) and drives:
//   • NarratorEngine.play(key)         — voice with subtitle
//   • TutorialMusic.openLowpass(...)   — at fade-in start
//   • Character.knockedOut / koStunRem — pin player asleep at start
//
// The coordinator is INTENTIONALLY runtime-side (not a sim Behavior). It
// needs DOM/Audio access (subtitles + WebAudio) and reads sim state through
// references handed in at construction. Sim itself stays unaware.

import type { NarratorEngine, NarratorPlayHandle } from './NarratorEngine';
import type { TutorialFlowBehavior } from '../behaviors/tutorialFlow';
import type { Character } from '../../../../sim/Character';

interface CoordinatorDeps {
  narrator: NarratorEngine;
  /** Called when we want music gain pulled all the way down (the player
   *  jumps into the pit before the closing narration finishes). The
   *  caller maps this to FxBus's music handle. */
  fadeMusicToZero: () => void;
  flow: TutorialFlowBehavior;
  player: Character;
  npc: Character;
}

type IntroPhase =
  | 'pre-fade'         // before fade-in begins; pin player asleep
  | 'asleep'           // 1_1_asleep playing; still pinned
  | 'awakening'        // 1_1 finished; 1 s grace before un-KO
  | 'awake-pre-talk'   // un-KO'd, 0.6 s pause before 1_2 starts
  | 'awake-talk'       // 1_2_awake playing
  | 'flow-driven'      // 1_2 finished; tutorialFlow.enablePhase1Walk() called; phase-based triggers active
  | 'closing'          // 6_2_follow_jimmy played; ramp music if player falls
  | 'done';

const BORED_DELAY_SEC = 30;
// 1 s pause between 1_1_asleep finishing and the player coming round.
const WAKE_DELAY_SEC = 1.0;
// 0.6 s pause between waking up and the wake-up line starting.
const POST_WAKE_TALK_DELAY_SEC = 0.6;
// Delay between a phase-advance "completion" and the next narrator line.
// Lets the door-open sound land cleanly before the dialogue starts, and
// gives the player a beat to register what they just did.
const PHASE_ENTER_NARRATION_DELAY_SEC = 0.6;

export class NarratorCoordinator {
  private state: IntroPhase = 'pre-fade';
  private current: NarratorPlayHandle | null = null;
  private idleSec = 0;
  private boredStage = 0; // 0=none, 1=played 0_1, 2=played 0_2, 3=played 0_3 (no more)
  private lastFlowPhase: number | string = 1;
  private wakeTimer = 0;
  private postWakeTalkTimer = 0;
  /** Pending phase-narration that hasn't started yet. Set when a phase
   *  advance lands a key in the schedule; cleared when the line actually
   *  starts (or when a newer phase advance pre-empts it). */
  private pendingNarration: { key: string; remainingSec: number } | null = null;
  // Phase-narrator gating — once a key has fired we don't replay it.
  private firedKeys = new Set<string>();
  // Keys whose audio has finished (or was cancelled). The hint banner reads
  // this to defer showing a phase's controls until after the relevant
  // narration line has played out.
  private finishedKeys = new Set<string>();
  // 6_1_2_careful is one-shot only.
  private carefulPlayed = false;
  // 6_2 unlocks the hole — track whether we've crossed that line yet.
  private holeUnlocked = false;
  private deps: CoordinatorDeps;

  constructor(deps: CoordinatorDeps) {
    this.deps = deps;
  }

  /** Begin the asleep-then-awaken sequence. Called from runTutorial right
   *  AFTER it has started the fade-from-black + opened the music low-pass
   *  (so the player hears music swell as they "come round"). */
  beginIntro(): void {
    this.state = 'asleep';
    this.current = this.deps.narrator.play('1_1_asleep');
    void this.current.done.then((outcome) => {
      if (outcome === 'finished' || outcome === 'cancelled') {
        this.wakeTimer = WAKE_DELAY_SEC;
        this.state = 'awakening';
      } else {
        // Audio failed — skip ahead so the tutorial isn't stuck.
        this.state = 'awake-talk';
        this.startAwakeTalk();
      }
    });
  }

  /** Tutorial wants to early-out (Skip button). Cancel any active line so
   *  the next screen isn't blocked by a 10-second monologue. */
  abort(): void {
    this.state = 'done';
    this.current?.cancel(150);
    this.current = null;
  }

  /** Whether the player is currently locked out of control by the
   *  narrator. Only true during the asleep / waking-up beat where the
   *  character is pinned KO'd — in awake-pre-talk and awake-talk the
   *  player already has full input control, the line just plays over
   *  the top, so pause should work. */
  isCinematicActive(): boolean {
    return this.state === 'pre-fade'
      || this.state === 'asleep'
      || this.state === 'awakening';
  }

  /** Player has crossed the pit edge (entered Room 6). Used while in the
   *  closing phase to ramp the music down ahead of the splash transition. */
  onPlayerCrossingPit(): void {
    if (this.state === 'closing') {
      this.deps.fadeMusicToZero();
    }
  }

  tick(dt: number): void {
    const flowPhase = this.deps.flow.phase;

    // Pin the player KO'd through the asleep period. We re-write the
    // values every frame so Character's tick can't decay them out of
    // state. As soon as the wake-up grace timer ends we stop re-pinning;
    // the awake-pre-talk state is silent input control just so the player
    // doesn't dart off before 1_2_awake starts.
    if (this.state === 'pre-fade' || this.state === 'asleep' || this.state === 'awakening') {
      this.deps.player.knockedOut = true;
      this.deps.player.koStunRemaining = 1.0;
    }

    if (this.state === 'awakening') {
      this.wakeTimer -= dt;
      if (this.wakeTimer <= 0) {
        this.deps.player.knockedOut = false;
        this.deps.player.koStunRemaining = 0;
        this.state = 'awake-pre-talk';
        this.postWakeTalkTimer = POST_WAKE_TALK_DELAY_SEC;
      }
    }

    if (this.state === 'awake-pre-talk') {
      this.postWakeTalkTimer -= dt;
      if (this.postWakeTalkTimer <= 0) {
        this.state = 'awake-talk';
        this.startAwakeTalk();
      }
    }

    // (no extra walk-wait state — tutorialFlow's own 2 s walkTimer takes
    // over the moment we call enablePhase1Walk() in startAwakeTalk's done
    // handler.)

    if (this.state === 'flow-driven' || this.state === 'closing') {
      // Pending phase narration — counts down the 0.6s delay we added so
      // the door-open sound has a beat before the dialogue begins.
      if (this.pendingNarration) {
        this.pendingNarration.remainingSec -= dt;
        if (this.pendingNarration.remainingSec <= 0) {
          const { key } = this.pendingNarration;
          this.pendingNarration = null;
          this.firePhaseNarration(key);
        }
      }

      // Idle / bored timer — accumulates while no phase advancement occurs.
      // A phase change resets the timer (the player is making progress).
      if (this.lastFlowPhase !== flowPhase) {
        this.idleSec = 0;
        this.lastFlowPhase = flowPhase;
        this.onPhaseEnter(flowPhase);
      } else if (this.boredStage < 3 && (this.current === null) && this.pendingNarration === null) {
        this.idleSec += dt;
        const stages = [BORED_DELAY_SEC, BORED_DELAY_SEC * 2, BORED_DELAY_SEC * 3];
        const nextThreshold = stages[this.boredStage]!;
        if (this.idleSec >= nextThreshold) {
          this.boredStage += 1;
          const key = `0_${this.boredStage}_bored`;
          this.current = this.deps.narrator.play(key);
          void this.current.done.then(() => { if (this.current?.key === key) this.current = null; });
        }
      }

      // Watch for npcDroppedIn → fire 6_2_follow_jimmy + open the hole.
      if (this.state === 'flow-driven' && this.firedKeys.has('6_1_throw_jimmy')
          && !this.firedKeys.has('6_2_follow_jimmy')) {
        const ny = this.deps.npc.ragdoll.pelvis().translation().y;
        // tutorialFlow's pit.npcDroppedY in the imported map is high-Y
        // (≈2.25 game). Anything below that means Timmy has dropped in.
        if (ny < 3.0) {
          this.firedKeys.add('6_2_follow_jimmy');
          this.cancelCurrent();
          this.current = this.deps.narrator.play('6_2_follow_jimmy');
          void this.current.done.then(() => {
            this.finishedKeys.add('6_2_follow_jimmy');
            if (this.current?.key === '6_2_follow_jimmy') this.current = null;
          });
          this.holeUnlocked = true;
          this.state = 'closing';
        }
      }

      // 6_1_2_careful — player walks into the rebound zone after 6_1 has
      // finished but before they've thrown Timmy. Fires once, and only if
      // no other dialogue is playing or pending (it's a soft warning, not
      // worth interrupting or queueing behind 6_1_throw_jimmy).
      if (!this.carefulPlayed
          && this.firedKeys.has('6_1_throw_jimmy')
          && !this.firedKeys.has('6_2_follow_jimmy')
          && this.current === null
          && this.pendingNarration === null) {
        const px = this.deps.player.ragdoll.pelvis().translation().x;
        const pitEdge = (this.deps.flow as unknown as { phase: unknown }).phase === 6
          ? this.getPitEdgeX()
          : Infinity;
        if (px > pitEdge - 0.6) {
          this.carefulPlayed = true;
          this.current = this.deps.narrator.play('6_1_2_careful');
          void this.current.done.then(() => { if (this.current?.key === '6_1_2_careful') this.current = null; });
        }
      }
    }
  }

  private startAwakeTalk(): void {
    this.current = this.deps.narrator.play('1_2_awake');
    void this.current.done.then(() => {
      this.finishedKeys.add('1_2_awake');
      // Hand off to tutorialFlow's own 2 s walkTimer.
      this.deps.flow.enablePhase1Walk();
      this.state = 'flow-driven';
      this.lastFlowPhase = this.deps.flow.phase;
      this.current = null;
    });
  }

  /** Has the narration line `key` finished playing (or been cancelled)?
   *  Used by the hint banner to fade hints in only after the narrator
   *  has explained what to do — same beat the player would naturally
   *  start trying the action. */
  hasFinished(key: string): boolean {
    return this.finishedKeys.has(key);
  }

  /** Hook on phase transitions. Schedules the line — actual play happens
   *  PHASE_ENTER_NARRATION_DELAY_SEC later via tick(). */
  private onPhaseEnter(phase: number | string): void {
    const schedule = (key: string): void => {
      if (this.firedKeys.has(key)) return;
      this.pendingNarration = { key, remainingSec: PHASE_ENTER_NARRATION_DELAY_SEC };
    };
    if (phase === 2) schedule('2_1_sprint');
    else if (phase === 3) schedule('3_1_run_jump');
    else if (phase === 4) schedule('4_1_wall_climb');
    else if (phase === 5) {
      // Reaching phase 5 means feet planted on top of the climb wall —
      // spec point for 5_1_jimmy_combat. Grab is intentionally left
      // available so the player can recover from falling off the ledge.
      schedule('5_1_jimmy_combat');
    } else if (phase === 6) {
      // Phase 6 means the combat narrator detected Jimmy is KO'd.
      schedule('6_1_throw_jimmy');
    } else if (phase === 'complete' || phase === 'skipped') {
      this.state = 'done';
      this.pendingNarration = null;
      this.current?.cancel(300);
    }
  }

  /** Actually start a phase-narration line. Called from tick() after the
   *  scheduled delay elapses. */
  private firePhaseNarration(key: string): void {
    if (this.firedKeys.has(key)) return;
    this.firedKeys.add(key);
    this.cancelCurrent();
    this.current = this.deps.narrator.play(key);
    void this.current.done.then(() => {
      this.finishedKeys.add(key);
      if (this.current?.key === key) this.current = null;
    });
  }

  private cancelCurrent(): void {
    if (this.current) {
      this.current.cancel(180);
      this.current = null;
    }
  }

  private getPitEdgeX(): number {
    // Reach into tutorialFlow's params via the public-but-undocumented
    // map.json shape. tutorialFlow exposes phase + room5HitsComplete only;
    // for pit edge we re-read the constant from the map by walking the
    // sim's snapshot — but a simpler shim is fine: hard-coded match to the
    // converter output (X≈42.75 at 0.25 scale, 21.4 at 0.125 etc.). Read
    // it off the rebound activation distance the flow already encodes.
    // The flow doesn't expose pit, so just use a generous fallback.
    return 42.0;
  }

  get isDone(): boolean { return this.state === 'done'; }
}
